import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ===================== ANIMATIONS =====================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.95) translateY(8px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const ripple = keyframes`
  0% { transform: scale(1); opacity: 0.4; }
  100% { transform: scale(2.5); opacity: 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-6px) rotate(1deg); }
  75% { transform: translateY(4px) rotate(-1deg); }
`;

const checkPop = keyframes`
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const slideRight = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.3); }
  70% { box-shadow: 0 0 0 10px rgba(2, 132, 199, 0); }
  100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
`;

// ===================== LAYOUT =====================
const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #f0f7ff 0%, #e8f1fb 30%, #f5f8fc 60%, #edf4ff 100%);
  padding: 32px 24px 80px;
  @media (max-width: 768px) { padding: 20px 14px 60px; }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 0.5s ease-out;
`;

// ===================== HERO =====================
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 32px;
  border-radius: 22px;
  overflow: hidden;
  background: linear-gradient(135deg, #0c4a6e 0%, #0369a1 35%, #0284c7 65%, #0ea5e9 100%);
  padding: 48px 52px;
  box-shadow:
    0 4px 6px rgba(3, 105, 161, 0.08),
    0 20px 50px rgba(3, 105, 161, 0.15);
  @media (max-width: 768px) { padding: 36px 24px; border-radius: 18px; }
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  svg { position: absolute; width: 100%; height: 100%; }
`;

const HeroGrid = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
  @media (max-width: 768px) { flex-direction: column; text-align: center; }
`;

const HeroLeft = styled.div`
  flex: 1;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 100px;
  padding: 6px 16px 6px 8px;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.5s ease 0.1s backwards;
`;

const BadgeDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #67e8f9;
  position: relative;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    inset: -3px;
    border-radius: 50%;
    background: #67e8f9;
    animation: ${ripple} 2s ease-out infinite;
  }
`;

const BadgeText = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  letter-spacing: 0.3px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.6px;
  margin: 0 0 12px;
  line-height: 1.2;
  animation: ${fadeIn} 0.5s ease 0.15s backwards;
  @media (max-width: 640px) { font-size: 1.6rem; }
`;

const HeroDescription = styled.p`
  font-size: 1.02rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.65;
  font-weight: 430;
  max-width: 440px;
  animation: ${fadeIn} 0.5s ease 0.2s backwards;
  @media (max-width: 768px) { max-width: 100%; }
`;

const HeroVisual = styled.div`
  flex-shrink: 0;
  width: 180px;
  height: 150px;
  animation: ${float} 6s ease-in-out infinite, ${fadeIn} 0.6s ease 0.3s backwards;
  @media (max-width: 768px) { width: 140px; height: 120px; }
`;

// ===================== ALERT BOX =====================
const AlertBox = styled.div`
  border-radius: 14px;
  padding: 18px 22px;
  margin-bottom: 24px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  animation: ${popIn} 0.4s cubic-bezier(0.22, 1, 0.36, 1);

  ${p => p.type === 'warning' && `
    background: linear-gradient(135deg, #fffbeb, #fef3c7);
    border: 2px solid #fde68a;
  `}
  ${p => p.type === 'info' && `
    background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
    border: 2px solid #7dd3fc;
  `}
  ${p => p.type === 'error' && `
    background: linear-gradient(135deg, #fef2f2, #fee2e2);
    border: 2px solid #fecaca;
  `}
  ${p => p.type === 'success' && `
    background: linear-gradient(135deg, #f0fdf4, #dcfce7);
    border: 2px solid #86efac;
  `}

  .alert-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.1rem;
    background: ${p => {
      if (p.type === 'warning') return '#fef3c7';
      if (p.type === 'error') return '#fee2e2';
      if (p.type === 'success') return '#dcfce7';
      return '#bae6fd';
    }};
  }

  .alert-content {
    flex: 1;
    strong {
      display: block;
      margin-bottom: 4px;
      font-size: 0.9rem;
      color: ${p => {
        if (p.type === 'warning') return '#92400e';
        if (p.type === 'error') return '#991b1b';
        if (p.type === 'success') return '#166534';
        return '#0c4a6e';
      }};
    }
    p {
      margin: 0;
      font-size: 0.85rem;
      line-height: 1.55;
      color: ${p => {
        if (p.type === 'warning') return '#a16207';
        if (p.type === 'error') return '#b91c1c';
        if (p.type === 'success') return '#15803d';
        return '#0369a1';
      }};
      font-weight: 450;
    }
  }
`;

// ===================== CARD =====================
const Card = styled.div`
  background: white;
  border-radius: 18px;
  padding: 28px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05);
  border: 1px solid rgba(0,0,0,0.04);
  margin-bottom: 24px;
  animation: ${fadeIn} 0.4s ease backwards;
  animation-delay: ${p => p.delay || '0s'};
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f9ff;

  .section-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: ${p => p.iconBg || 'linear-gradient(135deg, #e0f2fe, #bae6fd)'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1rem;
  }

  h2 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0c4a6e;
    margin: 0;
    flex: 1;
  }

  .badge {
    font-size: 0.72rem;
    font-weight: 700;
    padding: 4px 12px;
    border-radius: 100px;
    background: linear-gradient(135deg, #fff7ed, #fed7aa);
    color: #c2410c;
    border: 1px solid #fed7aa;
  }
`;

// ===================== STATUS ROW =====================
const StatusRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

const StatusItem = styled.div`
  animation: ${slideRight} 0.3s ease backwards;
  animation-delay: ${p => p.delay || '0s'};

  .label {
    font-size: 0.68rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 6px;
  }
  .value {
    font-size: 0.92rem;
    font-weight: 600;
    color: #1e293b;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 100px;
  font-size: 0.78rem;
  font-weight: 700;

  ${p => {
    switch(p.status) {
      case 'approved':
        return `background: linear-gradient(135deg, #f0fdf4, #dcfce7); color: #166534; border: 1px solid #86efac;`;
      case 'pending':
        return `background: linear-gradient(135deg, #fffbeb, #fef3c7); color: #92400e; border: 1px solid #fde68a;`;
      case 'rejected':
        return `background: linear-gradient(135deg, #fef2f2, #fee2e2); color: #991b1b; border: 1px solid #fecaca;`;
      default:
        return `background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;`;
    }
  }}
`;

// ===================== FORM ELEMENTS =====================
const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  animation: ${fadeIn} 0.3s ease backwards;
  animation-delay: ${p => p.delay || '0s'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 7px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.2px;
  .req { color: #ef4444; margin-left: 2px; }
`;

const Input = styled.input`
  width: 100%;
  padding: 11px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  font-weight: 500;
  box-sizing: border-box;

  &:hover:not(:disabled) { border-color: #bae6fd; }
  &:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
    background: white;
  }
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
  }
  &::placeholder { color: #94a3b8; font-weight: 400; }

  ${p => p.error && `border-color: #ef4444; &:focus { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1); }`}
`;

const Select = styled.select`
  width: 100%;
  padding: 11px 34px 11px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #1e293b;
  background: #f8fafc;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394a3b8' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  box-sizing: border-box;

  &:hover:not(:disabled) { border-color: #bae6fd; }
  &:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2,132,199,0.1);
    background-color: white;
  }
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 11px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #1e293b;
  background: #f8fafc;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  font-weight: 500;
  box-sizing: border-box;

  &:hover:not(:disabled) { border-color: #bae6fd; }
  &:focus {
    outline: none;
    border-color: #0284c7;
    box-shadow: 0 0 0 3px rgba(2, 132, 199, 0.1);
    background: white;
  }
  &:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    cursor: not-allowed;
  }
  &::placeholder { color: #94a3b8; font-weight: 400; }
`;

// ===================== FILE UPLOAD =====================
const FileUploadArea = styled.div`
  border: 2px dashed ${p => p.hasFile ? '#0284c7' : p.locked ? '#e2e8f0' : '#bae6fd'};
  border-radius: 14px;
  padding: 24px;
  text-align: center;
  cursor: ${p => p.locked ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  background: ${p => p.hasFile ? 'linear-gradient(135deg, #e0f2fe, #f0f9ff)' : p.locked ? '#f8fafc' : 'white'};
  opacity: ${p => p.locked ? 0.6 : 1};
  position: relative;
  overflow: hidden;

  &:hover {
    ${p => !p.locked && `
      border-color: #0284c7;
      background: #f0f9ff;
      transform: translateY(-1px);
    `}
  }

  .upload-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: ${p => p.hasFile ? 'linear-gradient(135deg, #0284c7, #0369a1)' : 'linear-gradient(135deg, #e0f2fe, #bae6fd)'};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 12px;
    transition: all 0.2s ease;
    font-size: 1.3rem;
    ${p => p.hasFile && `animation: ${checkPop} 0.3s ease;`}
  }

  .upload-title {
    font-weight: 650;
    color: ${p => p.hasFile ? '#0369a1' : '#334155'};
    margin-bottom: 4px;
    font-size: 0.92rem;
  }

  .upload-subtitle {
    color: #94a3b8;
    font-size: 0.82rem;
    font-weight: 450;
  }

  .current-file {
    margin-top: 14px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #dcfce7, #f0fdf4);
    border: 1px solid #86efac;
    border-radius: 8px;
    color: #166534;
    font-size: 0.82rem;
    font-weight: 600;
    display: inline-block;
  }

  .new-file {
    margin-top: 14px;
    padding: 8px 16px;
    background: linear-gradient(135deg, #e0f2fe, #f0f9ff);
    border: 1px solid #7dd3fc;
    border-radius: 8px;
    color: #0369a1;
    font-size: 0.82rem;
    font-weight: 600;
    display: inline-block;
  }

  .locked-msg {
    margin-top: 14px;
    padding: 8px 16px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    color: #64748b;
    font-size: 0.82rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

// ===================== SPECIALIZATION TAGS =====================
const SpecializationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.button`
  background: ${p => p.selected
    ? 'linear-gradient(135deg, #0284c7, #0369a1)'
    : '#f0f9ff'};
  color: ${p => p.selected ? 'white' : '#0369a1'};
  padding: 7px 14px;
  border-radius: 100px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  border: 2px solid ${p => p.selected ? 'transparent' : '#bae6fd'};
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  opacity: ${p => p.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    ${p => !p.selected && `
      background: #e0f2fe;
      border-color: #0284c7;
      transform: translateY(-1px);
    `}
    ${p => p.selected && `
      box-shadow: 0 2px 8px rgba(2, 132, 199, 0.3);
    `}
  }
`;

// ===================== DOCUMENTS DISPLAY =====================
const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const DocumentCard = styled.div`
  background: linear-gradient(135deg, #f0f9ff, #f8fafc);
  border: 2px solid #e0f2fe;
  border-radius: 14px;
  padding: 18px;
  text-align: center;
  transition: all 0.25s ease;
  animation: ${popIn} 0.35s ease backwards;
  animation-delay: ${p => p.delay || '0s'};

  &:hover {
    border-color: #7dd3fc;
    transform: translateY(-2px);
  }

  .doc-title {
    font-weight: 650;
    color: #0c4a6e;
    margin-bottom: 12px;
    font-size: 0.88rem;
  }

  .doc-preview {
    width: 100%;
    height: 130px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid #e0f2fe;
    transition: all 0.2s ease;

    &:hover {
      border-color: #0284c7;
      box-shadow: 0 2px 8px rgba(2, 132, 199, 0.1);
    }

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }

    .empty-icon {
      font-size: 2.2rem;
      opacity: 0.4;
    }
  }

  .doc-status {
    font-size: 0.78rem;
    font-weight: 600;
    color: #0284c7;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }
`;

// ===================== BUTTONS =====================
const Button = styled.button`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.2px;

  ${p => p.primary && `
    background: linear-gradient(135deg, #0284c7, #0369a1);
    color: white;
    box-shadow: 0 4px 14px rgba(2, 132, 199, 0.25);
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 22px rgba(2, 132, 199, 0.3);
    }
    &:active:not(:disabled) { transform: translateY(0); }
  `}

  ${p => p.warning && `
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: white;
    box-shadow: 0 4px 14px rgba(245, 158, 11, 0.25);
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 22px rgba(245, 158, 11, 0.3);
    }
  `}

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SaveFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  animation: ${fadeIn} 0.4s ease 0.3s backwards;

  .hint {
    font-size: 0.82rem;
    color: #94a3b8;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

// ===================== LOADING =====================
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  animation: ${fadeIn} 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid #e0f2fe;
  border-top-color: #0284c7;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const LoadingText = styled.p`
  font-size: 0.9rem;
  color: #94a3b8;
  font-weight: 500;
`;

// ===================== SVG ICONS =====================
const HeroBackgroundSVG = () => (
  <svg viewBox="0 0 800 200" preserveAspectRatio="none">
    <defs>
      <pattern id="heroGridProfile" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeOpacity="0.04" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#heroGridProfile)" />
    <circle cx="650" cy="40" r="60" fill="white" fillOpacity="0.03" />
    <circle cx="700" cy="140" r="40" fill="white" fillOpacity="0.04" />
    <circle cx="100" cy="160" r="80" fill="white" fillOpacity="0.02" />
  </svg>
);

const ProfileIllustration = () => (
  <svg viewBox="0 0 180 150" fill="none">
    {/* ID Card */}
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; 0,-4; 0,0" dur="4s" repeatCount="indefinite" />
      <rect x="20" y="30" width="90" height="65" rx="10" fill="white" fillOpacity="0.15" stroke="white" strokeOpacity="0.3" strokeWidth="1.5" />
      {/* Photo area */}
      <rect x="30" y="40" width="25" height="30" rx="5" fill="white" fillOpacity="0.1" stroke="white" strokeOpacity="0.2" strokeWidth="1" />
      <circle cx="42" cy="50" r="6" fill="white" fillOpacity="0.2" />
      <path d="M34 65 C34 60, 50 60, 50 65" stroke="white" strokeOpacity="0.2" strokeWidth="1" fill="none" />
      {/* Info lines */}
      <line x1="62" y1="44" x2="100" y2="44" stroke="white" strokeOpacity="0.3" strokeWidth="2" strokeLinecap="round" />
      <line x1="62" y1="52" x2="92" y2="52" stroke="white" strokeOpacity="0.2" strokeWidth="2" strokeLinecap="round" />
      <line x1="62" y1="60" x2="85" y2="60" stroke="white" strokeOpacity="0.15" strokeWidth="2" strokeLinecap="round" />
      {/* Badge */}
      <rect x="62" y="68" width="32" height="12" rx="6" fill="#67e8f9" fillOpacity="0.25" />
      <line x1="68" y1="74" x2="88" y2="74" stroke="#67e8f9" strokeOpacity="0.4" strokeWidth="1.5" strokeLinecap="round" />
    </g>

    {/* Shield / verification */}
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; 2,-5; 0,0" dur="3.5s" repeatCount="indefinite" />
      <path d="M135 35 L150 42 L150 55 C150 62, 145 68, 135 72 C125 68, 120 62, 120 55 L120 42 Z"
            fill="white" fillOpacity="0.12" stroke="white" strokeOpacity="0.3" strokeWidth="1.5" />
      <path d="M130 53 L134 57 L142 49" stroke="#67e8f9" strokeOpacity="0.7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <animate attributeName="strokeOpacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
      </path>
    </g>

    {/* Certificate */}
    <g>
      <animateTransform attributeName="transform" type="translate" values="0,0; -1,3; 0,0" dur="4.5s" repeatCount="indefinite" />
      <rect x="115" y="82" width="50" height="35" rx="6" fill="white" fillOpacity="0.1" stroke="white" strokeOpacity="0.25" strokeWidth="1" />
      <line x1="122" y1="92" x2="158" y2="92" stroke="white" strokeOpacity="0.2" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="122" y1="98" x2="150" y2="98" stroke="white" strokeOpacity="0.15" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="140" cy="108" r="5" fill="#67e8f9" fillOpacity="0.2" stroke="#67e8f9" strokeOpacity="0.3" strokeWidth="1" />
    </g>

    {/* Floating dots */}
    <circle cx="30" cy="115" r="4" fill="#67e8f9" fillOpacity="0.3">
      <animate attributeName="r" values="4;6;4" dur="2.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="80" cy="120" r="3" fill="white" fillOpacity="0.15">
      <animate attributeName="r" values="3;5;3" dur="3s" repeatCount="indefinite" />
    </circle>
  </svg>
);

// ===================== DATA =====================
const LICENSE_TYPES = [
  'Licensed Psychologist (PhD/PsyD)',
  'Licensed Clinical Social Worker (LCSW)',
  'Licensed Professional Counselor (LPC)',
  'Licensed Marriage and Family Therapist (LMFT)',
  'Licensed Mental Health Counselor (LMHC)',
  'Psychiatrist (MD)',
  'Psychiatric Nurse Practitioner',
  'Clinical Psychologist',
  'Counseling Psychologist',
  'Other'
];

const SPECIALIZATIONS = [
  'Clinical Psychology', 'Counseling Psychology', 'Child Psychology',
  'Neuropsychology', 'Forensic Psychology', 'Health Psychology',
  'Educational Psychology', 'Industrial-Organizational', 'Sports Psychology',
  'Addiction Counseling', 'Marriage & Family', 'Trauma & PTSD',
  'Anxiety & Depression', 'Eating Disorders', 'Personality Disorders'
];

// ===================== COMPONENT =====================
const Profile = () => {
  const { user, isApproved } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [uploadsLocked, setUploadsLocked] = useState(true);
  const [profileLocked, setProfileLocked] = useState(true);
  const [requestingChange, setRequestingChange] = useState(false);
  const [changeRequestPending, setChangeRequestPending] = useState(false);

  const cvInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  const profileImageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', phone: '',
    licenseNumber: '', licenseType: '', licenseState: '',
    licenseExpiry: '', yearsOfExperience: '', workplace: '',
    workplaceAddress: '', bio: '', specializations: []
  });

  const [files, setFiles] = useState({
    cv: null, licenseDocument: null, profileImage: null
  });

  useEffect(() => { loadProfile(); }, []);

  const getAuthenticatedFileUrl = (fileRef) => {
    if (!fileRef) return null;

    const tokenFromStorage =
      localStorage.getItem('token') || localStorage.getItem('admin_token');
    const tokenFromAxiosHeader = axios.defaults.headers.common.Authorization
      ?.replace(/^Bearer\s+/i, '')
      ?.trim();
    const token = tokenFromStorage || tokenFromAxiosHeader;

    const normalizedRef =
      typeof fileRef === 'string' && fileRef.includes('/')
        ? fileRef.replace(/^\/+/, '')
        : fileRef;

    const baseUrl = `${API_URL}/files/download/${normalizedRef}`;
    return token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/practitioner/profile`);
      const data = response.data;
      setProfile(data);
      setChangeRequestPending(data.changeRequestPending || false);
      const isLocked = !data.changeRequestApproved;
      setUploadsLocked(isLocked);
      setProfileLocked(isLocked);
      setFormData({
        firstName: data.firstName || '', lastName: data.lastName || '',
        phone: data.phone || '', licenseNumber: data.licenseNumber || '',
        licenseType: data.licenseType || '', licenseState: data.licenseState || '',
        licenseExpiry: data.licenseExpiry ? data.licenseExpiry.split('T')[0] : '',
        yearsOfExperience: data.yearsOfExperience || '', workplace: data.workplace || '',
        workplaceAddress: data.workplaceAddress || '', bio: data.bio || '',
        specializations: data.specializations || []
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (field === 'licenseDocument') {
      const allowed = ['application/pdf','image/jpeg','image/jpg','image/png','image/gif','image/webp'];
      if (!allowed.includes(file.type)) { toast.error('Please select a PDF or image file'); return; }
    } else if (field === 'cv') {
      if (file.type !== 'application/pdf') { toast.error('Please select a PDF file'); return; }
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be less than 5MB'); return; }
    setFiles(prev => ({ ...prev, [field]: file }));
    toast.success(`${field === 'cv' ? 'CV' : field === 'profileImage' ? 'Profile photo' : 'License document'} selected`);
  };

  const toggleSpecialization = (spec) => {
    if (profileLocked) return;
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'specializations') submitData.append(key, JSON.stringify(formData[key]));
        else submitData.append(key, formData[key] || '');
      });
      if (files.cv) submitData.append('cv', files.cv);
      if (files.licenseDocument) submitData.append('licenseDocument', files.licenseDocument);
      if (files.profileImage) submitData.append('profileImage', files.profileImage);
      await axios.put(`${API_URL}/practitioner/profile`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile updated successfully!');
      setFiles({ cv: null, licenseDocument: null, profileImage: null });
      setUploadsLocked(true);
      setProfileLocked(true);
      loadProfile();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save profile');
    } finally { setSaving(false); }
  };

  const handleRequestChange = async () => {
    try {
      setRequestingChange(true);
      await axios.post(`${API_URL}/practitioner/profile/request-change`, {
        message: 'I am requesting permission to update my profile documents.'
      });
      toast.success('Change request sent! Awaiting admin approval.');
      setChangeRequestPending(true);
      setUploadsLocked(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send change request');
    } finally { setRequestingChange(false); }
  };

  const isLicenseExpired = () => {
    if (!profile?.licenseExpiry) return false;
    return new Date(profile.licenseExpiry) < new Date();
  };

  const isLicenseExpiringSoon = () => {
    if (!profile?.licenseExpiry) return false;
    const exp = new Date(profile.licenseExpiry);
    const soon = new Date();
    soon.setDate(soon.getDate() + 30);
    return exp <= soon && exp > new Date();
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingWrapper>
            <LoadingSpinner />
            <LoadingText>Loading profile…</LoadingText>
          </LoadingWrapper>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        {/* Hero */}
        <HeroSection>
          <HeroBackground><HeroBackgroundSVG /></HeroBackground>
          <HeroGrid>
            <HeroLeft>
              <HeroBadge>
                <BadgeDot />
                <BadgeText>Professional Profile</BadgeText>
              </HeroBadge>
              <HeroTitle>My Profile</HeroTitle>
              <HeroDescription>
                Manage your account credentials, professional information, and verification documents.
              </HeroDescription>
            </HeroLeft>
            <HeroVisual>
              <ProfileIllustration />
            </HeroVisual>
          </HeroGrid>
        </HeroSection>

        {/* Alerts */}
        {profile?.approvalStatus === 'pending' && (
          <AlertBox type="warning">
            <div className="alert-icon">⏳</div>
            <div className="alert-content">
              <strong>Account Pending Approval</strong>
              <p>Your account is under review. You can update your credentials below if needed. Once approved, you'll have full access.</p>
            </div>
          </AlertBox>
        )}

        {profile?.approvalStatus === 'rejected' && (
          <AlertBox type="error">
            <div className="alert-icon">❌</div>
            <div className="alert-content">
              <strong>Application Rejected</strong>
              <p>{profile.rejectionReason || 'Your application was not approved.'} Please update your credentials and contact support.</p>
            </div>
          </AlertBox>
        )}

        {isLicenseExpired() && (
          <AlertBox type="error">
            <div className="alert-icon">🚨</div>
            <div className="alert-content">
              <strong>License Expired!</strong>
              <p>Your professional license has expired. Please upload a renewed license document to continue.</p>
            </div>
          </AlertBox>
        )}

        {isLicenseExpiringSoon() && !isLicenseExpired() && (
          <AlertBox type="warning">
            <div className="alert-icon">⚠️</div>
            <div className="alert-content">
              <strong>License Expiring Soon</strong>
              <p>Your license expires on {new Date(profile.licenseExpiry).toLocaleDateString()}. Please renew and update your credentials.</p>
            </div>
          </AlertBox>
        )}

        {/* Account Status */}
        <Card delay="0.05s">
          <SectionTitle>
            <div className="section-icon">📊</div>
            <h2>Account Status</h2>
          </SectionTitle>
          <StatusRow>
            <StatusItem delay="0.1s">
              <div className="label">Status</div>
              <StatusBadge status={profile?.approvalStatus}>
                {profile?.approvalStatus === 'approved' && '✓ Approved'}
                {profile?.approvalStatus === 'pending' && '⏳ Pending Review'}
                {profile?.approvalStatus === 'rejected' && '✗ Rejected'}
              </StatusBadge>
            </StatusItem>
            <StatusItem delay="0.15s">
              <div className="label">Email</div>
              <div className="value">{profile?.email}</div>
            </StatusItem>
            <StatusItem delay="0.2s">
              <div className="label">Member Since</div>
              <div className="value">{new Date(profile?.createdAt).toLocaleDateString()}</div>
            </StatusItem>
          </StatusRow>
        </Card>

        {/* Current Documents */}
        {profile?.approvalStatus === 'approved' && (
          <Card delay="0.1s">
            <SectionTitle>
              <div className="section-icon">📁</div>
              <h2>Your Current Documents</h2>
            </SectionTitle>

            <DocumentsGrid>
              <DocumentCard delay="0.15s">
                <div className="doc-title">Profile Photo</div>
                <div className="doc-preview">
                  {profile?.profileImagePath ? (
                    <img src={getAuthenticatedFileUrl(profile.profileImagePath)} alt="Profile" />
                  ) : (
                    <div className="empty-icon">👤</div>
                  )}
                </div>
                <div className="doc-status">
                  {profile?.profileImagePath ? '✓ Uploaded' : '— Not uploaded'}
                </div>
              </DocumentCard>

              <DocumentCard delay="0.2s">
                <div className="doc-title">License Document</div>
                <div className="doc-preview"
                  onClick={() => profile?.licenseDocumentPath && window.open(getAuthenticatedFileUrl(profile.licenseDocumentPath), '_blank')}
                >
                  {profile?.licenseDocumentPath ? (
                    <div className="empty-icon">📄</div>
                  ) : (
                    <div className="empty-icon">📄</div>
                  )}
                </div>
                <div className="doc-status">
                  {profile?.licenseDocumentPath ? '✓ Uploaded · Click to view' : '— Not uploaded'}
                </div>
              </DocumentCard>

              <DocumentCard delay="0.25s">
                <div className="doc-title">Curriculum Vitae</div>
                <div className="doc-preview"
                  onClick={() => profile?.cvPath && window.open(getAuthenticatedFileUrl(profile.cvPath), '_blank')}
                >
                  <div className="empty-icon">📋</div>
                </div>
                <div className="doc-status">
                  {profile?.cvPath ? '✓ Uploaded · Click to view' : '— Not uploaded'}
                </div>
              </DocumentCard>
            </DocumentsGrid>

            {changeRequestPending && (
              <AlertBox type="info">
                <div className="alert-icon">ℹ️</div>
                <div className="alert-content">
                  <strong>Change Request Pending</strong>
                  <p>Your request to update documents is awaiting admin approval.</p>
                </div>
              </AlertBox>
            )}

            {!changeRequestPending && (
              <div style={{ textAlign: 'center' }}>
                <Button warning onClick={handleRequestChange} disabled={requestingChange}>
                  {requestingChange ? '⏳ Sending Request…' : '📝 Request Permission to Update'}
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* License Credentials */}
        <Card delay="0.15s">
          <SectionTitle iconBg={isLicenseExpired() ? 'linear-gradient(135deg, #fee2e2, #fecaca)' : undefined}>
            <div className="section-icon">📜</div>
            <h2>License Credentials</h2>
            {(isLicenseExpired() || profile?.approvalStatus !== 'approved') && (
              <span className="badge">Update Required</span>
            )}
          </SectionTitle>

          <FormGrid>
            <FormGroup delay="0.2s">
              <Label>License Type <span className="req">*</span></Label>
              <Select name="licenseType" value={formData.licenseType} onChange={handleChange} disabled={profileLocked}>
                <option value="">Select license type</option>
                {LICENSE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </Select>
            </FormGroup>
            <FormGroup delay="0.22s">
              <Label>License Number <span className="req">*</span></Label>
              <Input name="licenseNumber" value={formData.licenseNumber} onChange={handleChange} placeholder="e.g., PSY-12345" disabled={profileLocked} />
            </FormGroup>
          </FormGrid>

          <FormGrid>
            <FormGroup delay="0.24s">
              <Label>License State/Region</Label>
              <Input name="licenseState" value={formData.licenseState} onChange={handleChange} placeholder="e.g., Greater Accra" disabled={profileLocked} />
            </FormGroup>
            <FormGroup delay="0.26s">
              <Label>License Expiry Date</Label>
              <Input type="date" name="licenseExpiry" value={formData.licenseExpiry} onChange={handleChange} disabled={profileLocked} error={isLicenseExpired()} />
              {isLicenseExpired() && (
                <span style={{ color: '#ef4444', fontSize: '0.78rem', fontWeight: 600, marginTop: 4, display: 'block' }}>
                  ⚠️ License has expired
                </span>
              )}
            </FormGroup>
          </FormGrid>

          <FormGroup delay="0.28s">
            <Label>Upload New License Document (PDF or Image)</Label>
            <FileUploadArea
              hasFile={!!files.licenseDocument}
              locked={uploadsLocked}
              onClick={() => !uploadsLocked && licenseInputRef.current?.click()}
            >
              <input ref={licenseInputRef} type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,application/pdf,image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => handleFileSelect(e, 'licenseDocument')} style={{ display: 'none' }} />
              <div className="upload-icon">{files.licenseDocument ? '✅' : '📄'}</div>
              <div className="upload-title">{files.licenseDocument ? 'New Document Selected' : 'Click to Upload License'}</div>
              <div className="upload-subtitle">PDF or image (JPG, PNG), maximum 5MB</div>
              {uploadsLocked && <div className="locked-msg">🔒 Uploads locked — request permission above</div>}
              {profile?.licenseDocumentPath && !files.licenseDocument && !uploadsLocked && (
                <div className="current-file">✓ Current document on file</div>
              )}
              {files.licenseDocument && (
                <div className="new-file">📎 {files.licenseDocument.name} ({(files.licenseDocument.size / 1024 / 1024).toFixed(2)} MB)</div>
              )}
            </FileUploadArea>
          </FormGroup>
        </Card>

        {/* Personal Information */}
        <Card delay="0.2s">
          <SectionTitle>
            <div className="section-icon">👤</div>
            <h2>Personal Information</h2>
          </SectionTitle>

          <FormGrid>
            <FormGroup delay="0.25s">
              <Label>First Name</Label>
              <Input name="firstName" value={formData.firstName} onChange={handleChange} disabled={profileLocked} />
            </FormGroup>
            <FormGroup delay="0.27s">
              <Label>Last Name</Label>
              <Input name="lastName" value={formData.lastName} onChange={handleChange} disabled={profileLocked} />
            </FormGroup>
          </FormGrid>

          <FormGroup delay="0.29s">
            <Label>Phone Number</Label>
            <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="+233 XX XXX XXXX" disabled={profileLocked} />
          </FormGroup>
        </Card>

        {/* Professional Information */}
        <Card delay="0.25s">
          <SectionTitle>
            <div className="section-icon">🏥</div>
            <h2>Professional Information</h2>
          </SectionTitle>

          <FormGroup delay="0.3s">
            <Label>Profile Photo</Label>
            <FileUploadArea
              hasFile={!!files.profileImage}
              locked={uploadsLocked}
              onClick={() => !uploadsLocked && profileImageInputRef.current?.click()}
            >
              <input ref={profileImageInputRef} type="file" accept="image/*"
                onChange={(e) => handleFileSelect(e, 'profileImage')} style={{ display: 'none' }} />
              <div className="upload-icon">{files.profileImage ? '✅' : '📷'}</div>
              <div className="upload-title">{files.profileImage ? 'New Photo Selected' : 'Click to Upload Profile Photo'}</div>
              <div className="upload-subtitle">JPG, PNG, GIF or WebP, maximum 5MB</div>
              {uploadsLocked && <div className="locked-msg">🔒 Uploads locked — request permission to change</div>}
              {files.profileImage && (
                <div className="new-file">📎 {files.profileImage.name} ({(files.profileImage.size / 1024 / 1024).toFixed(2)} MB)</div>
              )}
            </FileUploadArea>
          </FormGroup>

          <FormGroup delay="0.32s">
            <Label>Years of Experience</Label>
            <Input type="number" name="yearsOfExperience" value={formData.yearsOfExperience} onChange={handleChange} placeholder="e.g., 5" min="0" disabled={profileLocked} />
          </FormGroup>

          <FormGroup delay="0.34s">
            <Label>Current Workplace</Label>
            <Input name="workplace" value={formData.workplace} onChange={handleChange} placeholder="e.g., Korle Bu Teaching Hospital" disabled={profileLocked} />
          </FormGroup>

          <FormGroup delay="0.36s">
            <Label>Workplace Address</Label>
            <TextArea name="workplaceAddress" value={formData.workplaceAddress} onChange={handleChange} placeholder="Full address" style={{ minHeight: '80px' }} disabled={profileLocked} />
          </FormGroup>

          <FormGroup delay="0.38s">
            <Label>Professional Bio</Label>
            <TextArea name="bio" value={formData.bio} onChange={handleChange} placeholder="Tell us about your background and expertise..." disabled={profileLocked} />
          </FormGroup>

          <FormGroup delay="0.4s">
            <Label>Areas of Specialization</Label>
            <SpecializationTags>
              {SPECIALIZATIONS.map(spec => (
                <Tag key={spec} type="button"
                  selected={formData.specializations.includes(spec)}
                  disabled={profileLocked}
                  onClick={() => toggleSpecialization(spec)}
                >
                  {formData.specializations.includes(spec) ? '✓ ' : ''}{spec}
                </Tag>
              ))}
            </SpecializationTags>
          </FormGroup>

          <FormGroup delay="0.42s">
            <Label>Update CV (PDF)</Label>
            <FileUploadArea
              hasFile={!!files.cv}
              locked={uploadsLocked}
              onClick={() => !uploadsLocked && cvInputRef.current?.click()}
            >
              <input ref={cvInputRef} type="file" accept=".pdf,application/pdf"
                onChange={(e) => handleFileSelect(e, 'cv')} style={{ display: 'none' }} />
              <div className="upload-icon">{files.cv ? '✅' : '📋'}</div>
              <div className="upload-title">{files.cv ? 'New CV Selected' : 'Click to Upload CV'}</div>
              <div className="upload-subtitle">PDF format, maximum 5MB</div>
              {uploadsLocked && <div className="locked-msg">🔒 Uploads locked — request permission to change</div>}
              {files.cv && <div className="new-file">📎 {files.cv.name}</div>}
            </FileUploadArea>
          </FormGroup>
        </Card>

        {/* Save Footer */}
        <SaveFooter>
          {!uploadsLocked && (
            <Button primary onClick={handleSave} disabled={saving}>
              {saving ? '⏳ Saving…' : '💾 Save Changes'}
            </Button>
          )}
          {uploadsLocked && profile?.approvalStatus === 'approved' && !changeRequestPending && (
            <div className="hint">
              🔒 Uploads are locked. Click "Request Permission to Update" above to make changes.
            </div>
          )}
        </SaveFooter>
      </Container>
    </PageWrapper>
  );
};

export default Profile;
